import type { UnionToIntersection } from '@direct-frontend/stdlib';
import { configureStore } from '@reduxjs/toolkit';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector';
import { createDynamicMiddleware } from './createDynamicMiddleware';
import { handleErrorMiddleware } from './handleErrorMiddleware';

import { buildCreateSlice, BuiltCreateSlice, UnsafeBuiltCreateSlice } from './buildCreateSlice';
import { buildCreateApi, BuiltCreateApi } from './buildCreateApi';
import { buildCreateAsyncThunk, BuiltCreateAsyncThunk } from './buildCreateAsyncThunk';
import {
    registerDevToolsStore,
    getDevtoolsEnhancer,
    composedStoreMiddleware,
    isReduxDevToolsAvailable,
} from './composedStoreDevTools';
import { createBatchedSubscription } from './selectors/createBatchedSubscription';
import { createDynamicReducer } from './createDynamicReducer';
import { storeManager } from './storeManager';
import type {
    EqualityFn,
    GetStoreFn,
    ModuleState,
    ModuleStore,
    UseDispatchType,
    UseSelectorType,
    UseStoreType,
    AnyStorePlugin,
} from './types';

export const $INTERNALS = Symbol('$INTERNALS');

/**
 * Базовая реализация функции сравнения нового и старого состояний в `useSelector()`
 * Сравнивает два значения по ссылке
 */
const refEquality: EqualityFn<unknown> = (a, b) => a === b;

/**
 * Опции модульного стора
 */
export type CreateStoreOptions<Plugins> = {
    plugins: Plugins;
};

/**
 * Инстанс модульного стора
 */
export type CreatedStore<ModuleName extends string> = {
    moduleName: ModuleName;
    createSlice: BuiltCreateSlice;
    /**
     * Устаревший метод для создания слайса в модульном сторе. Метод не учитывает
     * JSON-совместимость стейта слайса. Используйте метод createSlice.
     * @deprecated
     */
    unsafeCreateSlice: UnsafeBuiltCreateSlice;
    createAsyncThunk: BuiltCreateAsyncThunk;
    [$INTERNALS]: {
        getStore: GetStoreFn;
        createApi: BuiltCreateApi<ModuleName>;
    };
};

/**
 * Список модулей, созданных через createStore().
 */
const registeredModules = new Set<string>();

/**
 * Результат плагинов, который будет добавлен к итоговому объекту стора.
 */
type PluginsResult<Plugins extends AnyStorePlugin[]> = UnionToIntersection<ReturnType<Plugins[number]>>;

export function createStore<M extends string, P extends AnyStorePlugin[]>(
    moduleName: M,
    options: CreateStoreOptions<P>
): CreatedStore<M> & PluginsResult<P>;
export function createStore<M extends string>(moduleName: M): CreatedStore<M>;
export function createStore<M extends string, P extends AnyStorePlugin[]>(
    moduleName: M,
    options?: CreateStoreOptions<P>
): CreatedStore<M> & PluginsResult<AnyStorePlugin[]> {
    // Список плагинов для стора
    const storePlugins: AnyStorePlugin[] = options?.plugins ?? [];
    // Подключение редьюсеров в рантайме
    const { getDynamicReducer, injectReducer } = createDynamicReducer(moduleName, () => getStore());
    // Подключение мидлвар в рантайме
    const { dynamicMiddleware, injectMiddleware } = createDynamicMiddleware(moduleName, () => getStore());

    if (registeredModules.has(moduleName)) {
        throw new Error(`Store named '${moduleName}' has been already registered.`);
    }

    registeredModules.add(moduleName);

    /**
     * Ленивая обертка для получения текущего стора модуля. Нужна, чтобы сторы, созданные при SSR,
     * не шарили свое состояние при разных запросах к серверу.
     */
    const getStore: GetStoreFn = () => {
        const createdStore = storeManager.getStore(moduleName);
        if (createdStore) {
            return createdStore;
        }

        const store = createStore();

        if (process.env.BUILD_TARGET === 'client') {
            registerDevToolsStore({ name: moduleName, store });
        }

        storeManager.setStore(moduleName, store);
        return store;
    };

    /**
     * Создает инстанс модульного стора
     */
    const createStore = (): ModuleStore => {
        const store = configureStore({
            reducer: getDynamicReducer(),
            middleware: getDefaultMiddlewares => {
                const defaultMiddlewares = getDefaultMiddlewares({
                    immutableCheck: false,
                    serializableCheck: false,
                }).prepend(dynamicMiddleware);

                if (process.env.BUILD_TARGET === 'client' && isReduxDevToolsAvailable()) {
                    defaultMiddlewares.push(composedStoreMiddleware);
                }

                defaultMiddlewares.push(handleErrorMiddleware);

                return defaultMiddlewares;
            },
            devTools: false,
            enhancers: getDefaultEnhancers => {
                const defaultEnhancers = getDefaultEnhancers({ autoBatch: false });
                if (process.env.BUILD_TARGET === 'client') {
                    const devToolsEnhancer = getDevtoolsEnhancer({
                        name: moduleName,
                        title: `${window.origin} – Module: ${moduleName}`,
                    });

                    if (devToolsEnhancer) {
                        defaultEnhancers.push(devToolsEnhancer);
                    }
                }
                return defaultEnhancers;
            },
        });

        return Object.assign(store, {
            moduleName,
            subscribeBatched: createBatchedSubscription(store),
        });
    };

    /**
     * Модульный хук useDispatch.
     * Используется для создания правильного билдера RTK Query API.
     */
    const useDispatch: UseDispatchType = () => getStore().dispatch;

    /**
     * Модульный хук useStore.
     * Используется для создания правильного билдера RTK Query API.
     */
    const useStore: UseStoreType = () => getStore();

    /**
     * Модульный хук useSelector.
     * Используется для получения данных из модульного стора в разных частях приложения.
     */
    const useSelector: UseSelectorType = (selector, equalityFn = refEquality) => {
        const store = getStore();

        return useSyncExternalStoreWithSelector<ModuleState, ReturnType<typeof selector>>(
            store.subscribeBatched,
            store.getState,
            store.getState,
            selector,
            equalityFn
        );
    };

    // Выполняем все плагины, чтобы получить их результат и пропатчить возвращаемый объект.
    const storePluginsResults = storePlugins.reduce<PluginsResult<AnyStorePlugin[]>>(
        (result, storePlugin) =>
            Object.assign(
                result,
                storePlugin({
                    moduleName,
                    getStore,
                    injectMiddleware,
                    injectReducer,
                    useDispatch,
                    useSelector,
                    useStore,
                })
            ),
        {}
    );

    // Создает слайс в модульном сторе
    const createSlice = buildCreateSlice({ moduleName, getStore, injectReducer });

    return {
        ...storePluginsResults,
        moduleName,
        createSlice,
        // Не меняем реализацию метода для создания слайса. Отличие только в типах —
        // createSlice требует JSON-совместимого типа стейта.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
        // @ts-expect-error -- см. выше
        unsafeCreateSlice: createSlice,
        createAsyncThunk: buildCreateAsyncThunk({
            moduleName,
            getStore,
        }),
        [$INTERNALS]: {
            getStore,
            createApi: buildCreateApi({
                moduleName,
                getStore,
                injectMiddleware,
                injectReducer,
                useDispatch,
                useSelector,
                useStore,
            }),
        },
    };
}

/**
 * Сбрасывает все зарегистрированные сторы. Используется только для тестов.
 * @internal
 */
export function cleanupRegisteredModules() {
    registeredModules.clear();
}
